import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Eye, Settings, Table, Grid, ArrowLeft } from 'lucide-react';

type TableVariant = 'basic' | 'striped' | 'bordered' | 'hover' | 'compact' | 'spacious';
type TableStyle = 'minimal' | 'corporate' | 'data-focused' | 'comparison' | 'dashboard';

interface TableDefinition {
  name: string;
  category: string;
  description: string;
  variants: TableVariant[];
  defaultData: any[][];
  headers: string[];
}

interface TableDesignerState {
  selectedTable: TableDefinition | null;
  selectedVariant: TableVariant;
  selectedStyle: TableStyle;
  borderWidth: number;
  borderColor: string;
  headerBackground: string;
  headerTextColor: string;
  cellPadding: number;
  rowSpacing: number;
  fontSize: number;
  fontFamily: string;
  alternatingRows: boolean;
  hoverEffect: boolean;
  borderRadius: number;
  shadowIntensity: number;
}

const tableDefinitions: TableDefinition[] = [
  {
    name: 'Data Table',
    category: 'Data',
    description: 'Standard data presentation with sortable columns',
    variants: ['basic', 'striped', 'bordered', 'hover'],
    headers: ['Name', 'Role', 'Department', 'Status', 'Last Active'],
    defaultData: [
      ['Sarah Chen', 'Product Manager', 'Product', 'Active', '2 hours ago'],
      ['Mike Rodriguez', 'Engineering Lead', 'Engineering', 'Active', '1 hour ago'],
      ['Lisa Park', 'Design Director', 'Design', 'Away', '3 hours ago'],
      ['David Kim', 'Data Analyst', 'Analytics', 'Active', '30 min ago'],
      ['Emma Wilson', 'Marketing Manager', 'Marketing', 'Active', '1 hour ago']
    ]
  },
  {
    name: 'Comparison Table',
    category: 'Comparison',
    description: 'Feature comparison or pricing tables',
    variants: ['bordered', 'striped', 'hover'],
    headers: ['Feature', 'Basic Plan', 'Pro Plan', 'Enterprise'],
    defaultData: [
      ['Users', 'Up to 5', 'Up to 50', 'Unlimited'],
      ['Storage', '10GB', '100GB', '1TB'],
      ['Support', 'Email', 'Priority', '24/7 Phone'],
      ['API Access', 'Limited', 'Full', 'Full + Custom'],
      ['Price', '$29/month', '$99/month', 'Contact Sales']
    ]
  },
  {
    name: 'Metrics Dashboard',
    category: 'Dashboard',
    description: 'KPI and metric display tables',
    variants: ['compact', 'striped', 'hover'],
    headers: ['Metric', 'Current', 'Target', 'Trend', 'Status'],
    defaultData: [
      ['Team Alignment', '87%', '90%', '↗ +5%', 'On Track'],
      ['Decision Speed', '2.3 days', '2.0 days', '↘ -0.5 days', 'Behind'],
      ['Project Velocity', '12.5 pts', '15.0 pts', '↗ +1.2 pts', 'Improving'],
      ['Stakeholder Satisfaction', '4.2/5', '4.5/5', '↗ +0.3', 'Good'],
      ['Budget Utilization', '78%', '85%', '↗ +3%', 'On Track']
    ]
  },
  {
    name: 'Task List',
    category: 'Task Management',
    description: 'Project tasks and status tracking',
    variants: ['basic', 'striped', 'hover'],
    headers: ['Task', 'Assignee', 'Priority', 'Due Date', 'Status'],
    defaultData: [
      ['Design System Audit', 'Lisa Park', 'High', 'Dec 15', 'In Progress'],
      ['User Research Synthesis', 'Sarah Chen', 'Medium', 'Dec 12', 'Completed'],
      ['API Documentation', 'Mike Rodriguez', 'High', 'Dec 18', 'Not Started'],
      ['Brand Guidelines Update', 'Emma Wilson', 'Low', 'Dec 20', 'In Progress'],
      ['Performance Optimization', 'David Kim', 'Medium', 'Dec 16', 'In Progress']
    ]
  },
  {
    name: 'Framework Matrix',
    category: 'Frameworks',
    description: 'Peak Priorities framework comparison',
    variants: ['bordered', 'striped', 'hover'],
    headers: ['Framework', 'Use Case', 'Complexity', 'Time to Implement', 'Effectiveness'],
    defaultData: [
      ['7 Cs Model', 'Strategic Clarity', 'Low', '1-2 weeks', 'High'],
      ['DEGAP Process', 'Team Alignment', 'Medium', '2-4 weeks', 'High'],
      ['State Classification', 'Decision Making', 'Medium', '1-3 weeks', 'High'],
      ['Cost of Delay', 'Prioritization', 'Low', '1 week', 'Medium'],
      ['Compass Watermark', 'Direction Setting', 'Low', '3-5 days', 'Medium']
    ]
  }
];

const colorPresets = [
  { name: 'Sage (Primary)', value: '#5A7D5E' },
  { name: 'Alpine (Secondary)', value: '#53675B' },
  { name: 'Charcoal (Neutral)', value: '#9CA3AF' },
  { name: 'Mustard (Accent)', value: '#E3B505' },
  { name: 'Coral (Alert)', value: '#F85E5E' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#F6F8FA' },
  { name: 'Dark Gray', value: '#2B2F32' },
  // Experimental Askew Complements
  { name: 'Ocean (Depth)', value: '#2F5C5B' },
  { name: 'Terracotta (Trail)', value: '#C97A5B' },
  { name: 'Ochre (Light)', value: '#E8B86A' },
  { name: 'Frosted (Sky)', value: '#AFC6D9' },
  // Experimental Structural Gap Fillers
  { name: 'Burnt (Bridge)', value: '#D86B2D' },
  { name: 'Olive (Field)', value: '#6C7142' },
  { name: 'Violet (Shadow)', value: '#756D8A' },
  { name: 'Sand (Base)', value: '#D8C6A8' }
];

const fontFamilies = [
  { name: 'Fieldwork (Headings)', value: 'fieldwork, sans-serif' },
  { name: 'Degular Variable (Body)', value: 'degular-variable, -apple-system, BlinkMacSystemFont, sans-serif' },
  { name: 'IBM Plex Serif (Quotes)', value: 'ibm-plex-serif, serif' },
  { name: 'System UI', value: 'system-ui, sans-serif' },
  { name: 'Monospace', value: 'ui-monospace, monospace' }
];

const stylePresets = {
  'minimal': {
    borderWidth: 0,
    borderColor: '#E5E7EB',
    headerBackground: '#F9FAFB',
    headerTextColor: '#374151',
    cellPadding: 12,
    rowSpacing: 0,
    fontSize: 14,
    fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, sans-serif',
    alternatingRows: false,
    hoverEffect: false,
    borderRadius: 0,
    shadowIntensity: 0
  },
  'corporate': {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    headerBackground: '#5A7D5E',
    headerTextColor: '#FFFFFF',
    cellPadding: 16,
    rowSpacing: 1,
    fontSize: 14,
    fontFamily: 'fieldwork, sans-serif',
    alternatingRows: true,
    hoverEffect: true,
    borderRadius: 4,
    shadowIntensity: 1
  },
  'data-focused': {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    headerBackground: '#53675B',
    headerTextColor: '#FFFFFF',
    cellPadding: 12,
    rowSpacing: 0,
    fontSize: 13,
    fontFamily: 'ui-monospace, monospace',
    alternatingRows: true,
    hoverEffect: true,
    borderRadius: 0,
    shadowIntensity: 0
  },
  'comparison': {
    borderWidth: 2,
    borderColor: '#5A7D5E',
    headerBackground: '#E3B505',
    headerTextColor: '#1F2937',
    cellPadding: 20,
    rowSpacing: 2,
    fontSize: 16,
    fontFamily: 'fieldwork, sans-serif',
    alternatingRows: true,
    hoverEffect: true,
    borderRadius: 8,
    shadowIntensity: 2
  },
  'dashboard': {
    borderWidth: 0,
    borderColor: '#E5E7EB',
    headerBackground: '#F6F8FA',
    headerTextColor: '#374151',
    cellPadding: 8,
    rowSpacing: 0,
    fontSize: 12,
    fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, sans-serif',
    alternatingRows: true,
    hoverEffect: true,
    borderRadius: 6,
    shadowIntensity: 1
  }
};

export const TableDesigner: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [designerState, setDesignerState] = useState<TableDesignerState>({
    selectedTable: null,
    selectedVariant: 'basic',
    selectedStyle: 'minimal',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    headerBackground: '#F9FAFB',
    headerTextColor: '#374151',
    cellPadding: 12,
    rowSpacing: 0,
    fontSize: 14,
    fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, sans-serif',
    alternatingRows: false,
    hoverEffect: false,
    borderRadius: 0,
    shadowIntensity: 0
  });

  const categories = ['All', ...Array.from(new Set(tableDefinitions.map(table => table.category)))];

  const filteredTables = tableDefinitions.filter(table => {
    const matchesSearch = table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         table.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || table.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleTableSelect = (table: TableDefinition) => {
    setDesignerState(prev => ({
      ...prev,
      selectedTable: table,
      selectedVariant: table.variants[0]
    }));
  };

  const handleVariantChange = (variant: TableVariant) => {
    setDesignerState(prev => ({ ...prev, selectedVariant: variant }));
  };

  const handleStyleChange = (style: TableStyle) => {
    const preset = stylePresets[style];
    setDesignerState(prev => ({
      ...prev,
      selectedStyle: style,
      ...preset
    }));
  };

  const handlePropertyChange = (property: keyof TableDesignerState, value: any) => {
    setDesignerState(prev => ({ ...prev, [property]: value }));
  };

  const generateTableStyles = () => {
    const {
      borderWidth,
      borderColor,
      rowSpacing,
      fontSize,
      fontFamily,
      borderRadius,
      shadowIntensity
    } = designerState;

    const shadowStyles = {
      0: 'none',
      1: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      2: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      3: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    };

    return {
      border: `${borderWidth}px solid ${borderColor}`,
      borderRadius: `${borderRadius}px`,
      boxShadow: shadowStyles[shadowIntensity as keyof typeof shadowStyles],
      fontFamily,
      fontSize: `${fontSize}px`,
      borderCollapse: 'separate' as const,
      borderSpacing: `${rowSpacing}px 0`
    };
  };

  const generateCellStyles = () => {
    const { cellPadding, alternatingRows, hoverEffect } = designerState;
    
    return {
      padding: `${cellPadding}px`,
      borderBottom: alternatingRows ? '1px solid #E5E7EB' : 'none',
      transition: hoverEffect ? 'background-color 0.2s ease' : 'none'
    };
  };

  const generateHeaderStyles = () => {
    const { headerBackground, headerTextColor, cellPadding } = designerState;
    
    return {
      backgroundColor: headerBackground,
      color: headerTextColor,
      padding: `${cellPadding}px`,
      fontWeight: '600',
      textAlign: 'left' as const,
      borderBottom: '2px solid #E5E7EB'
    };
  };

  const generateRowStyles = (index: number) => {
    const { alternatingRows, hoverEffect } = designerState;
    
    const baseStyles = {
      backgroundColor: alternatingRows && index % 2 === 1 ? '#F9FAFB' : 'transparent'
    };

    if (hoverEffect) {
      return {
        ...baseStyles,
        ':hover': {
          backgroundColor: '#F3F4F6'
        }
      };
    }

    return baseStyles;
  };

  const generateCode = () => {
    if (!designerState.selectedTable) return '';

    const {
      borderWidth,
      borderColor,
      headerBackground,
      headerTextColor,
      cellPadding,
      rowSpacing,
      fontSize,
      fontFamily,
      alternatingRows,
      hoverEffect,
      borderRadius,
      shadowIntensity
    } = designerState;

    const shadowStyles = {
      0: 'none',
      1: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      2: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      3: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
    };

    return `const tableStyles = {
  border: '${borderWidth}px solid ${borderColor}',
  borderRadius: '${borderRadius}px',
  boxShadow: '${shadowStyles[shadowIntensity as keyof typeof shadowStyles]}',
  fontFamily: '${fontFamily}',
  fontSize: '${fontSize}px',
  borderCollapse: 'separate',
  borderSpacing: '${rowSpacing}px 0'
};

const headerStyles = {
  backgroundColor: '${headerBackground}',
  color: '${headerTextColor}',
  padding: '${cellPadding}px',
  fontWeight: '600',
  textAlign: 'left',
  borderBottom: '2px solid #E5E7EB'
};

const cellStyles = {
  padding: '${cellPadding}px',
  borderBottom: '${alternatingRows ? '1px solid #E5E7EB' : 'none'}',
  transition: '${hoverEffect ? 'background-color 0.2s ease' : 'none'}'
};`;
  };

  const copyToClipboard = async () => {
    const code = generateCode();
    try {
      await navigator.clipboard.writeText(code);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/elevation/studio" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700">
              <ArrowLeft className="h-4 w-4" />
              Back to Studio
            </Link>
            <span className="text-charcoal-400">•</span>
            <span className="text-sm text-charcoal-600">Studio Engine</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[var(--surface)]">
              <Table className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-charcoal-900">Table Designer</h1>
              <p className="text-charcoal-600">Create and customize table designs for the Peak Priorities design system</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel - Table Selection */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
              <h2 className="text-xl font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
                <Grid className="w-5 h-5" />
                Table Templates
              </h2>

              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search tables..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                />
              </div>

              {/* Category Filter */}
              <div className="mb-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Table List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredTables.map((table) => (
                  <div
                    key={table.name}
                    onClick={() => handleTableSelect(table)}
                    className={`p-3  border-2 cursor-pointer transition-all ${
                      designerState.selectedTable?.name === table.name
                        ? 'border-[var(--rule)] bg-[var(--surface)]'
                        : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
                    }`}
                  >
                    <h3 className="font-medium text-charcoal-900">{table.name}</h3>
                    <p className="text-sm text-charcoal-600">{table.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {table.variants.map(variant => (
                        <span
                          key={variant}
                          className="px-2 py-1 text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] rounded"
                        >
                          {variant}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Center Panel - Preview */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
              <h2 className="text-xl font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Live Preview
              </h2>

              {designerState.selectedTable ? (
                <div className="space-y-4">
                  {/* Variant Selector */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Variant
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {designerState.selectedTable.variants.map(variant => (
                        <button
                          key={variant}
                          onClick={() => handleVariantChange(variant)}
                          className={`px-3 py-1 text-sm  border transition-all ${
                            designerState.selectedVariant === variant
                              ? 'bg-[var(--surface)] text-white border-[var(--rule)]'
                              : 'bg-[var(--bg-raised)] text-charcoal-700 border-[var(--rule)] hover:border-[var(--rule)]'
                          }`}
                        >
                          {variant}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Table Preview */}
                  <div className="overflow-x-auto">
                    <table style={generateTableStyles()}>
                      <thead>
                        <tr>
                          {designerState.selectedTable.headers.map((header, index) => (
                            <th key={index} style={generateHeaderStyles()}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {designerState.selectedTable.defaultData.map((row, rowIndex) => (
                          <tr
                            key={rowIndex}
                            style={generateRowStyles(rowIndex)}
                            className={designerState.hoverEffect ? 'hover:bg-[var(--hover-tint)]' : ''}
                          >
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} style={generateCellStyles()}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-charcoal-500">
                  <Table className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a table template to start customizing</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Customization */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
              <h2 className="text-xl font-semibold text-charcoal-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Customization
              </h2>

              {designerState.selectedTable ? (
                <div className="space-y-6">
                  {/* Style Presets */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Style Presets
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.keys(stylePresets).map(style => (
                        <button
                          key={style}
                          onClick={() => handleStyleChange(style as TableStyle)}
                          className={`px-3 py-2 text-sm  border transition-all ${
                            designerState.selectedStyle === style
                              ? 'bg-[var(--surface)] text-white border-[var(--rule)]'
                              : 'bg-[var(--bg-raised)] text-charcoal-700 border-[var(--rule)] hover:border-[var(--rule)]'
                          }`}
                        >
                          {style}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Border Settings */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Border Width: {designerState.borderWidth}px
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="4"
                      value={designerState.borderWidth}
                      onChange={(e) => handlePropertyChange('borderWidth', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Border Color
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {colorPresets.slice(0, 8).map(color => (
                        <button
                          key={color.name}
                          onClick={() => handlePropertyChange('borderColor', color.value)}
                          className={`w-8 h-8 rounded border-2 ${
                            designerState.borderColor === color.value ? 'border-charcoal-800' : 'border-[var(--rule)]'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Header Settings */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Header Background
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {colorPresets.map(color => (
                        <button
                          key={color.name}
                          onClick={() => handlePropertyChange('headerBackground', color.value)}
                          className={`w-8 h-8 rounded border-2 ${
                            designerState.headerBackground === color.value ? 'border-charcoal-800' : 'border-[var(--rule)]'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Header Text Color
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {colorPresets.map(color => (
                        <button
                          key={color.name}
                          onClick={() => handlePropertyChange('headerTextColor', color.value)}
                          className={`w-8 h-8 rounded border-2 ${
                            designerState.headerTextColor === color.value ? 'border-charcoal-800' : 'border-[var(--rule)]'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Spacing Settings */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Cell Padding: {designerState.cellPadding}px
                    </label>
                    <input
                      type="range"
                      min="4"
                      max="32"
                      value={designerState.cellPadding}
                      onChange={(e) => handlePropertyChange('cellPadding', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Row Spacing: {designerState.rowSpacing}px
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="8"
                      value={designerState.rowSpacing}
                      onChange={(e) => handlePropertyChange('rowSpacing', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Typography Settings */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Font Size: {designerState.fontSize}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="20"
                      value={designerState.fontSize}
                      onChange={(e) => handlePropertyChange('fontSize', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Font Family
                    </label>
                    <select
                      value={designerState.fontFamily}
                      onChange={(e) => handlePropertyChange('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      {fontFamilies.map(font => (
                        <option key={font.name} value={font.value}>{font.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Effects */}
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={designerState.alternatingRows}
                        onChange={(e) => handlePropertyChange('alternatingRows', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-charcoal-700">Alternating Row Colors</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={designerState.hoverEffect}
                        onChange={(e) => handlePropertyChange('hoverEffect', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-charcoal-700">Hover Effects</span>
                    </label>
                  </div>

                  {/* Border Radius */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Border Radius: {designerState.borderRadius}px
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="16"
                      value={designerState.borderRadius}
                      onChange={(e) => handlePropertyChange('borderRadius', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Shadow */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Shadow Intensity: {designerState.shadowIntensity}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="3"
                      value={designerState.shadowIntensity}
                      onChange={(e) => handlePropertyChange('shadowIntensity', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {/* Code Generation */}
                  <div className="pt-4 border-t">
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={copyToClipboard}
                        className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--bg-raised)] transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        Copy CSS
                      </button>
                    </div>
                    <div className="bg-[var(--hover-tint)] p-3">
                      <pre className="text-xs text-charcoal-700 overflow-x-auto">
                        {generateCode()}
                      </pre>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-charcoal-500">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a table to customize</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
